import { Alert, Button, Group, Loader, Modal, Select, Stack, Text } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { RequestType } from "@msc/shared";

import { isJiraReauthRequired, useCustomerViewConnection } from "../../hooks/useCustomerView.js";
import {
  useCreateTicket,
  useCustomerContacts,
  useRequestTypeForm,
  useRequestTypes,
} from "../../hooks/useCustomerViewActions.js";
import { writeErrorText } from "../../lib/customerView/jiraValidation.js";
import { type Eingabe, istLeer, MaskenFeld, maskenwerte } from "./FeldMaske.js";
import { ReconnectBanner } from "./ReconnectBanner.js";

/**
 * Auswahlliste der Anfragetypen nach Portalgruppe. Ein Typ, der in mehreren
 * Gruppen steht, erscheint unter der ersten — Mantine verlangt eindeutige
 * Werte, und doppelt angeboten hilft niemandem.
 */
export function anfragetypGruppen(typen: RequestType[]): { group: string; items: { value: string; label: string }[] }[] {
  const gruppen = new Map<string, { value: string; label: string }[]>();
  for (const typ of typen) {
    const name = typ.groups[0] ?? "";
    gruppen.set(name, [...(gruppen.get(name) ?? []), { value: typ.id, label: typ.name }]);
  }
  return [...gruppen].map(([group, items]) => ({ group, items }));
}

/**
 * Neues Ticket im Termin (#271), wie im Kundenportal: Anfragetyp, dessen
 * Felder, Melder. Nur im Cockpit — die Bühne sieht davon nichts, bis das
 * Ticket im Deck steht.
 *
 * Der Melder ist ein Kundenkontakt, kein Mitarbeiter: Jira leitet
 * Kundenfirma und Kundenabteilung aus ihm ab. Unter dem eigenen Namen
 * angelegt, gehörte das Ticket zu keinem Kunden und erschiene nie im Deck.
 */
export function NewTicketModal({
  opened,
  onClose,
  objectKey,
  customerName,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  objectKey: string;
  customerName: string;
  onCreated: (key: string, customerDepartmentSet: boolean) => void;
}) {
  const { t } = useTranslation();
  const [typId, setTypId] = useState<string | null>(null);
  const [werte, setWerte] = useState<Record<string, Eingabe>>({});
  const [melder, setMelder] = useState<string | null>(null);

  const typen = useRequestTypes(opened);
  const maske = useRequestTypeForm(typId);
  const kontakte = useCustomerContacts(objectKey, opened);
  const create = useCreateTicket(objectKey);
  const site = useCustomerViewConnection().data?.site ?? null;

  const typ = typen.data?.requestTypes.find((r) => r.id === typId) ?? null;
  const felder = maske.data?.fields ?? [];
  const nurJira = maske.data?.jiraOnlyRequired ?? [];
  const fuerKontakt = maske.data?.canRaiseOnBehalfOf === true;
  const kontaktListe = kontakte.data?.contacts ?? [];
  const melderFehlt = fuerKontakt && kontaktListe.length > 0 && melder === null;
  const kannAnlegen =
    !!maske.data && nurJira.length === 0 && !melderFehlt && felder.every((f) => !f.required || !istLeer(werte[f.key]));

  const schliessen = () => {
    if (create.isPending) return;
    setTypId(null);
    setWerte({});
    setMelder(null);
    create.reset();
    onClose();
  };

  const anlegen = () => {
    if (!typId) return;
    create.mutate(
      {
        requestTypeId: typId,
        fields: maskenwerte(felder, werte, {}),
        ...(fuerKontakt && melder ? { raiseOnBehalfOf: melder } : {}),
      },
      {
        onSuccess: (r) => {
          onCreated(r.key, r.customerDepartmentSet);
          schliessen();
        },
      },
    );
  };

  const fehler = typen.error ?? maske.error ?? kontakte.error ?? null;

  return (
    <Modal
      opened={opened}
      onClose={schliessen}
      title={t("pages.customerView.newTicket.title", { customer: customerName })}
      size="lg"
      closeOnClickOutside={!create.isPending}
      withCloseButton={!create.isPending}
    >
      <Stack gap="sm">
        {site && (
          <Text size="xs" c="dimmed">
            {t("pages.customerView.newTicket.realTicket", { host: site.host })}
          </Text>
        )}
        {fehler &&
          (isJiraReauthRequired(fehler) ? (
            <ReconnectBanner />
          ) : (
            <Alert color="red" variant="light">
              {t("pages.customerView.newTicket.loadFailed")}
            </Alert>
          ))}
        <Select
          label={t("pages.customerView.newTicket.requestType")}
          placeholder={typen.isLoading ? t("common.loading") : t("pages.customerView.newTicket.requestTypePick")}
          data={anfragetypGruppen(typen.data?.requestTypes ?? [])}
          searchable
          required
          value={typId}
          onChange={(v) => {
            setTypId(v);
            setWerte({});
            create.reset();
          }}
          disabled={create.isPending}
        />
        {typ?.description && (
          <Text size="xs" c="dimmed">
            {typ.description}
          </Text>
        )}
        {typId && maske.isLoading && (
          <Group gap={6}>
            <Loader size="xs" />
            <Text size="sm">{t("common.loading")}</Text>
          </Group>
        )}
        {nurJira.length > 0 && (
          <Alert color="yellow" variant="light">
            {t("pages.customerView.newTicket.onlyInJira", { fields: nurJira.map((f) => f.name).join(", ") })}
          </Alert>
        )}
        {maske.data && nurJira.length === 0 && (
          <>
            {fuerKontakt && kontaktListe.length > 0 && (
              <Select
                label={t("pages.customerView.newTicket.reporter")}
                description={t("pages.customerView.newTicket.reporterHint")}
                placeholder={t("pages.customerView.newTicket.reporterPick")}
                data={kontaktListe.map((k) => ({ value: k.accountId, label: k.displayName }))}
                searchable
                required
                value={melder}
                onChange={setMelder}
                disabled={create.isPending}
              />
            )}
            {(!fuerKontakt || (kontakte.isSuccess && kontaktListe.length === 0)) && (
              <Alert color="yellow" variant="light">
                {t(fuerKontakt ? "pages.customerView.newTicket.noContacts" : "pages.customerView.newTicket.notOnBehalf")}
              </Alert>
            )}
            {felder.map((f) => (
              <MaskenFeld key={f.key} feld={f} wert={werte[f.key]} onChange={(v) => setWerte((alt) => ({ ...alt, [f.key]: v }))} />
            ))}
          </>
        )}
        {create.isError &&
          (isJiraReauthRequired(create.error) ? (
            <ReconnectBanner />
          ) : (
            <Alert color="red" variant="light">
              {writeErrorText(create.error, t, "pages.customerView.newTicket.failed")}
            </Alert>
          ))}
        <Group justify="flex-end">
          <Button variant="default" size="xs" disabled={create.isPending} onClick={schliessen}>
            {t("pages.customerView.cockpit.actions.cancel")}
          </Button>
          <Button size="xs" loading={create.isPending} disabled={!kannAnlegen} onClick={anlegen}>
            {t("pages.customerView.newTicket.create")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
